import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/MomentBar.css';

export default function MomentBar() {
  const [moments, setMoments] = useState([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const limitRef = React.useRef(4);
  const skipRef = React.useRef(0);
  const loadingRef = React.useRef(false);
  const hasMoreRef = React.useRef(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // fetch a page of moments (pagination for horizontal lazy loading)
  const fetchMomentsPage = (reset = false) => {
    const token = localStorage.getItem('jwt');
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!token || !user) return;
    if (!reset && (loadingRef.current || !hasMoreRef.current)) return;
    if (reset) { skipRef.current = 0; hasMoreRef.current = true; setMoments([]); }
    loadingRef.current = true;
    setLoadingMore(true);
    const url = `/moments?limit=${limitRef.current}&skip=${skipRef.current}`;
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => { if (r.status === 401) return null; return r.json(); })
      .then(list => {
        if (!list) return;
        const currentUserId = user._id;
        const mapped = list.map(m => {
          const viewRecord = m.viewedSlides && m.viewedSlides.find(v => v.user.toString() === currentUserId);
          const viewedIndices = viewRecord ? viewRecord.slideIndices : [];
          const hasUnviewed = m.media && m.media.length > 0 && viewedIndices.length < m.media.length;
          return { ...m, hasUnviewed };
        });
        setMoments(prev => {
          const existingIds = new Set(prev.map(p => p._id));
          const merged = [...prev];
          for (const m of mapped) {
            if (!existingIds.has(m._id)) {
              merged.push(m);
              existingIds.add(m._id);
            } else {
              // update status for existing item without changing order
              for (let i = 0; i < merged.length; i++) {
                if (merged[i]._id === m._id) { merged[i] = { ...merged[i], hasUnviewed: m.hasUnviewed }; break; }
              }
            }
          }
          return merged;
        });
        const count = Array.isArray(list) ? list.length : 0;
        skipRef.current += count;
        if (count < limitRef.current) { hasMoreRef.current = false; }
      })
      .catch(() => {})
      .finally(() => { loadingRef.current = false; setLoadingMore(false); });
  };

  useEffect(() => {
    // initial load of first page
    fetchMomentsPage(true);

    // horizontal lazy-load: listen for scroll on the moment bar container
    const container = document.querySelector('.moment-bar');
    const onScroll = () => {
      if (!container) return;
      const nearEnd = container.scrollWidth - (container.scrollLeft + container.clientWidth) < 120;
      if (nearEnd) fetchMomentsPage();
    };
    if (container) container.addEventListener('scroll', onScroll);

    // Refetch statuses every 2 seconds but only update `hasUnviewed` flags to avoid reordering
    const fetchStatuses = () => {
      const token = localStorage.getItem('jwt');
      if (!token) return;
      fetch('/moments', { headers: { Authorization: 'Bearer ' + token } })
        .then(r => { if (r.status === 401) return null; return r.json(); })
        .then(list => {
          if (!list) return;
          const currentUserId = user && user._id;
          const map = new Map();
          for (const m of list) {
            const viewRecord = m.viewedSlides && m.viewedSlides.find(v => v.user.toString() === currentUserId);
            const viewedIndices = viewRecord ? viewRecord.slideIndices : [];
            const hasUnviewed = m.media && m.media.length > 0 && viewedIndices.length < m.media.length;
            map.set(m._id, hasUnviewed);
          }
          setMoments(prev => {
            const prevIds = new Set(prev.map(p => p._id));
            const updated = prev.map(p => ({ ...(p), hasUnviewed: map.has(p._id) ? map.get(p._id) : p.hasUnviewed }));
            // append any new moments not already present (keep them appended to preserve order)
            for (const m of list) {
              if (!prevIds.has(m._id)) {
                const viewRecord = m.viewedSlides && m.viewedSlides.find(v => v.user.toString() === currentUserId);
                const viewedIndices = viewRecord ? viewRecord.slideIndices : [];
                const hasUnviewed = m.media && m.media.length > 0 && viewedIndices.length < m.media.length;
                updated.push({ ...m, hasUnviewed });
              }
            }
            return updated;
          });
        }).catch(() => {});
    };

    const interval = setInterval(fetchStatuses, 2000);

    // Listen for moment-slide-viewed event and update that specific moment immediately
    const handleMomentViewed = (e) => {
      const detail = e && e.detail ? e.detail : {};
      const momentId = detail.momentId;
      if (!momentId) { fetchStatuses(); return; }
      // recompute for that moment by fetching single moment status (fallback to full list)
      fetch('/moments', { headers: { Authorization: 'Bearer ' + localStorage.getItem('jwt') } })
        .then(r => r.ok ? r.json() : null)
        .then(list => {
          if (!list) return;
          const m = list.find(x => x._id === momentId);
          if (!m) return;
          const currentUserId = user && user._id;
          const viewRecord = m.viewedSlides && m.viewedSlides.find(v => v.user.toString() === currentUserId);
          const viewedIndices = viewRecord ? viewRecord.slideIndices : [];
          const hasUnviewed = m.media && m.media.length > 0 && viewedIndices.length < m.media.length;
          setMoments(prev => prev.map(p => p._id === momentId ? { ...p, hasUnviewed } : p));
        }).catch(() => {});
    };
    window.addEventListener('moment-slide-viewed', handleMomentViewed);

    // Listen for explicit status updates so we can update the specific moment instantly
    const handleStatusChanged = (e) => {
      const detail = e && e.detail ? e.detail : {};
      const momentId = detail.momentId;
      if (!momentId) return;
      if (typeof detail.viewedCount === 'number' && typeof detail.totalSlides === 'number') {
        const hasUnviewed = detail.viewedCount < detail.totalSlides;
        setMoments(prev => prev.map(m => m._id === momentId ? { ...m, hasUnviewed } : m));
      } else if (typeof detail.hasUnviewed === 'boolean') {
        setMoments(prev => prev.map(m => m._id === momentId ? { ...m, hasUnviewed: detail.hasUnviewed } : m));
      } else {
        fetchStatuses();
      }
    };
    window.addEventListener('moment-status-changed', handleStatusChanged);

    return () => {
      clearInterval(interval);
      if (container) container.removeEventListener('scroll', onScroll);
      window.removeEventListener('moment-slide-viewed', handleMomentViewed);
      window.removeEventListener('moment-status-changed', handleStatusChanged);
    };
  }, []);

  const openMoment = (id) => {
    navigate(`/moment/${id}`);
  };

  return (
    <div className="moment-bar">
      {user && (
        <div className="moment-item" onClick={() => navigate('/createMoment')}>
          <div className="moment-avatar add">
            <span className="material-symbols-outlined">add</span>
          </div>
          <div className="moment-name">Your Moments</div>
        </div>
      )}
      {moments.map(m => (
        <div key={m._id} className="moment-item" onClick={() => openMoment(m._id)}>
          <img className="moment-avatar" src={m.owner.Photo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iNTAiIGZpbGw9IiNkZGQiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIxNiIgZmlsbD0iI2FhYSIvPjxwYXRoIGQ9Ik0yOCA2MCBRNTAgNzAgNzIgNjAiIGZpbGw9IiNhYWEiLz48L3N2Zz4='} alt="" style={{ border: m.hasUnviewed ? '3px solid #FF6B3B' : '2px solid #ddd' }} />
          <div className="moment-name">{m.owner.name}</div>
        </div>
      ))}
      {loadingMore && (
        <div className="moment-bar-loader">
          <div className="spinner" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}